import React, { useMemo } from 'react';
import { Box, formatSnakeCaseToGeneral, Inscription, ListItem, ListItemField, OverflowText, RawButton } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import { capitalize } from 'lodash';
import styled from 'styled-components';

import { REGEXPS } from '@/common';
import { DEFAULT_DATE_FORMAT, LetterIconSizeEnum, OrganizationTypes } from '@/common/constants';
import Permissions from '@/common/permissions';
import { ListItemType } from '@/common/types';
import { DropButton } from '@/components/controls/DropButton';
import { Icon } from '@/components/elements/Icon/Icon';
import { useHasAccess } from '@/modules/core';
import { DependentStatusEnum } from '@/modules/employee/employee.types';
import theme from '@/styles/theme';

import DependentsAge from './DependentsAge';
import { useGetDependentsQuery } from './useDependents.query';

const EXTRA_TYPE_PART = 'WEB_';
const HIDDEN_DEPENDENT = 'Hidden Dependent';
const FIELD_PLACEHOLDER = '-';

interface MenuOptionButtonProps {
  $isWarning?: boolean;
}

const MenuOptionButton = styled(RawButton)<MenuOptionButtonProps>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  width: 100%;
  border-radius: 4px;
  padding: ${({ theme }) => `${theme.spacings.spacing6} ${theme.spacings.spacing8}`};

  &:hover {
    background: ${({ theme, $isWarning }) =>
      $isWarning ? theme.colors.dangerContainer : theme.colors.accentContainer};
  }
`;

interface Props {
  employeeId?: string;
  setActivationDependentId?: React.Dispatch<React.SetStateAction<string>>;
  setEditDependentId?: React.Dispatch<React.SetStateAction<string>>;
  selectedStatuses?: DependentStatusEnum[];
}

const useDependentsList = ({
  employeeId,
  setActivationDependentId,
  setEditDependentId,
  selectedStatuses,
}: Props) => {
  const { dependents, isLoading } = useGetDependentsQuery(employeeId, selectedStatuses);
  const hasPermissionToEditDependent = useHasAccess([{ permission: Permissions.EDIT_DEP_DATA}]);

  const dependentsWithoutHidden = useMemo(() => dependents
    .filter((dependent) => `${dependent.firstName} ${dependent.lastName}` !== HIDDEN_DEPENDENT
      && dependent.fullName !== HIDDEN_DEPENDENT), [dependents]);

  const formattedFields = useMemo<ListItem[]>(() => dependentsWithoutHidden.map((item) => {
    const isInactive = item?.status === DependentStatusEnum.INACTIVE;

    return {
      id: `${item?.dependentId}`,
      fields: [
        {
          key: 'fullName',
          type: ListItemType.ErLogo,
          organizationType: OrganizationTypes.dependent,
          title: item?.fullName || '',
          flex: 1.5,
          logo: (
            <Icon
              type={OrganizationTypes.dependent}
              text={item?.fullName}
              size={LetterIconSizeEnum.LOGO}
            />
          ),
          node: (
            <Box margin={{ left: 'spacing12' }}>
              <OverflowText title={item?.fullName} textStyle={{ fontWeight: 'bold' }} />
            </Box>
          ),
        },
        {
          key: 'dateOfBirth',
          type: ListItemType.Node,
          title: '',
          node: (
            <Box direction="row" align="center">
              <Inscription size="medium" color="textBody">
                {item?.birthOn ? dayjs(item.birthOn).format(DEFAULT_DATE_FORMAT) : FIELD_PLACEHOLDER}
              </Inscription>
              {item?.birthOn ? <DependentsAge birthOn={item.birthOn} /> : null}
            </Box>
          ),
          flex: 1,
        },
        {
          key: 'relationship',
          type: ListItemType.Text,
          value: item.relationshipType || '',
          title: formatSnakeCaseToGeneral(item?.relationshipType || ''),
          fieldStyle: { alignItems: 'flex-start', flex: '1' },
        },
        {
          key: 'source',
          type: ListItemType.Text,
          value: item.creationSourceType || '',
          title: capitalize(item?.creationSourceType?.replace(EXTRA_TYPE_PART, '')?.replace(REGEXPS.DASH_SYMBOL, ' ')),
          fieldStyle: { alignItems: 'flex-start', flex: '1' },
        },
        {
          key: 'status',
          type: ListItemType.Status,
          title: capitalize(item?.status?.replace(REGEXPS.DASH_SYMBOL, ' ')),
          flex: 1,
        },
        ...hasPermissionToEditDependent ? [
          {
            key: 'options',
            type: ListItemType.Node,
            title: '',
            flex: 'none',
            fieldStyle: { right: theme.spacings.spacing24, position: 'absolute' },
            node: (
              <DropButton
                testId="dependentsListOptions"
                contentStyle={{
                  minWidth: '150px',
                  top: isInactive ? '-75px' : '-95px',
                  left: '-120px',
                }}
              >
                {isInactive ? null : (
                  <MenuOptionButton
                    data-testid={`dependentsListOptions_${item?.dependentId}_edit`}
                    onClick={() => {
                      setEditDependentId?.(item?.dependentId || '');
                    }}
                  >
                    <Inscription
                      cursor="pointer"
                      color="textAccent"
                      size="14px"
                      lineHeight="20px"
                      weight={400}
                    >
                      Edit
                    </Inscription>
                  </MenuOptionButton>
                )}

                <MenuOptionButton
                  data-testid={`dependentsListOptions_${item?.dependentId}_${isInactive ? 'activate' : 'deactivate'}`}
                  $isWarning={!isInactive}
                  onClick={() => {
                    setActivationDependentId?.(item?.dependentId || '');
                  }}
                >
                  <Inscription
                    color={isInactive ? theme.colors.textBody : theme.colors.danger}
                    cursor="pointer"
                    size="14px"
                    lineHeight="20px"
                    weight={400}
                  >
                    {isInactive ? 'Activate' : 'Deactivate'}
                  </Inscription>
                </MenuOptionButton>
              </DropButton>
            ),
          },
        ] : [],
      ] as ListItemField[],
    };
  }) || [], [
      dependentsWithoutHidden,
      setActivationDependentId,
      setEditDependentId,
      hasPermissionToEditDependent,
    ]);

  return {
    data: formattedFields,
    formattedDependents: dependentsWithoutHidden,
    isLoading,
  };
};

export default useDependentsList;
